import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { PostcardWriter } from '../components/checkout/PostcardWriter';
import { OrderReview } from '../components/checkout/OrderReview';
import { PostcardTemplateData } from '../types/postcard';
import { getFileUrl, postcardApi } from '../services/api';
import { SenderAddress } from '../components/checkout/SenderAddress';
import { Address } from '../types/address';

const steps = ['Write Postcards', 'Sender Address', 'Review Order'];

export const Checkout: React.FC = () => {
  const { postcardId } = useParams<{ postcardId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [frontImage, setFrontImage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [postcards, setPostcards] = useState<PostcardTemplateData[]>([]);
  const [senderAddress, setSenderAddress] = useState<Address>({
    name: '',
    address_line1: '',
    address_line2: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: '',
  });

  // Load postcard data
  useEffect(() => {
    const loadPostcard = async () => {
      if (!postcardId) return;
      
      try {
        setLoading(true);
        const response = await postcardApi.getById(postcardId);
        
        // Set front image
        setFrontImage(getFileUrl(response.data.imageUrl));
      } catch (error) {
        console.error('Error loading postcard:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPostcard();
  }, [postcardId]);
  

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handlePostcardsChange = (newPostcards: PostcardTemplateData[]) => {
    setPostcards(newPostcards.map((postcard, index) => ({
      ...postcard,
      id: `${postcardId}-${index + 1}`,
    })));
  };

  const handleSenderAddressChange = (field: string, value: string) => {
    setSenderAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handlePaymentSuccess = () => {
    navigate('/success');
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <PostcardWriter
            postcardImage={frontImage}
            onPostcardsChange={handlePostcardsChange}
            initialPostcards={postcards}
          />
        );
      case 1:
        return (
          <SenderAddress
            senderAddress={senderAddress}
            onSenderAddressChange={handleSenderAddressChange}
          />
        );
      case 2:
        return (
          <OrderReview
            postcardId={postcardId!}
            postcards={postcards}
            senderAddress={senderAddress}
            onSuccess={handlePaymentSuccess}
          />
        );
      default:
        return null;
    }
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 0:
        return postcards.length > 0 && postcards.every(
          (postcard) => postcard.message && 
                        postcard.recipientAddress.name &&
                        postcard.recipientAddress.address_line1 &&
                        postcard.recipientAddress.address_city &&
                        postcard.recipientAddress.address_state &&
                        postcard.recipientAddress.address_zip
        );
      case 1:
        return senderAddress.name && senderAddress.address_line1 && 
               senderAddress.address_city && senderAddress.address_state && 
               senderAddress.address_zip;
      case 2:
        return true; // Payment validation is handled within the Payment component
      default:
        return false;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom align="center">
          Checkout
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
          >
            Back
          </Button>
          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid(activeStep) || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Next'}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
}; 